import { addDays, differenceInCalendarDays, parseISO } from 'date-fns'
import { makeDayKey, parseCalendarDate } from '../monthGrid'
import { type CalendarItem, type CalendarItemInMarket } from '../types'

/**
 * The only thing that can stop a drop. Content belongs to its moment, so it
 * cannot start before the moment does. Landing after the moment ends is allowed:
 * the moment stretches to cover it.
 */
export type CalendarDropForbiddenReason = 'before-moment'

/** New dates for one item. `end` is inclusive, as it is on `CalendarItem`. */
export type CalendarDateChange = {
  id: string
  kind: CalendarItem['kind']
  /** So a message about the move can name what moved. */
  title: string
  /** `yyyy-MM-dd`. */
  start: string
  /** `yyyy-MM-dd`. */
  end: string
  /** ISO 8601 instant, when the item carries a time of day. */
  startsAt?: string
  inMarket?: CalendarItemInMarket
}

export type CalendarDropProposal = {
  /**
   * How many days the item moved. Every date on it moves by the same number,
   * which is what keeps the length of a moment and the in-market window of a
   * piece of content intact. Undoing the move is the same number, negated.
   */
  days: number
  /** What the person dragged, on its new dates. */
  moved: CalendarDateChange
  /** Where it was, so the move can be undone without another fetch. */
  movedFrom: CalendarDateChange
  /**
   * The parent moment, stretched so it still covers the content that just
   * landed outside it, with the dates it had before.
   */
  parent?: { next: CalendarDateChange; previous: CalendarDateChange }
  forbiddenReason: CalendarDropForbiddenReason | null
}

const toChange = (item: CalendarItem): CalendarDateChange => ({
  end: item.end,
  id: item.id,
  inMarket: item.inMarket,
  kind: item.kind,
  start: item.start,
  startsAt: item.startsAt,
  title: item.title,
})

const shiftDay = (day: string, days: number, id: string): string | null => {
  const parsed = parseCalendarDate(day, { field: 'day', id })
  return parsed === null ? null : makeDayKey(addDays(parsed, days))
}

/**
 * Moves an instant by whole days, which keeps the clock time it goes live at.
 * Without this a 3pm post dragged to another day would quietly become midnight.
 */
const shiftInstant = (startsAt: string, days: number): string =>
  addDays(parseISO(startsAt), days).toISOString()

/**
 * Works out what dropping `item` on `targetDate` would mean.
 *
 * Everything keeps its length: a three-day moment stays three days, and a piece
 * of content keeps both its time of day and its in-market window. Returns null
 * when there is nothing to do — the item was dropped back where it started, or
 * its dates cannot be read.
 *
 * `parent` is the moment the content belongs to, when the calendar is showing
 * it. Without it neither the guard nor the stretch can be worked out, so the
 * drop is allowed: content whose moment is off screen almost always belongs to
 * a moment that started earlier.
 */
export const buildDropProposal = ({
  item,
  parent,
  targetDate,
}: {
  item: CalendarItem
  parent?: CalendarItem
  targetDate: Date
}): CalendarDropProposal | null => {
  const start = parseCalendarDate(item.start, { field: 'start', id: item.id })
  if (start === null) return null

  const days = differenceInCalendarDays(targetDate, start)
  if (days === 0) return null

  const nextStart = makeDayKey(targetDate)
  const nextEnd = shiftDay(item.end, days, item.id)
  if (nextEnd === null) return null

  // A window that follows the moment ends where the moment does, which only a
  // stretch below can change.
  const nextInMarket =
    item.inMarket === undefined
      ? undefined
      : item.inMarket.followsMoment
        ? { ...item.inMarket, start: nextStart }
        : {
            end: shiftDay(item.inMarket.end, days, item.id),
            start: shiftDay(item.inMarket.start, days, item.id),
          }
  if (
    nextInMarket &&
    (nextInMarket.end === null || nextInMarket.start === null)
  )
    return null

  const moved: CalendarDateChange = {
    end: nextEnd,
    id: item.id,
    inMarket: nextInMarket as CalendarItemInMarket | undefined,
    kind: item.kind,
    start: nextStart,
    title: item.title,
    startsAt:
      item.startsAt === undefined
        ? undefined
        : shiftInstant(item.startsAt, days),
  }

  const proposal: CalendarDropProposal = {
    days,
    forbiddenReason:
      item.kind === 'content' &&
      parent !== undefined &&
      nextStart < parent.start
        ? 'before-moment'
        : null,
    moved,
    movedFrom: toChange(item),
  }

  if (proposal.forbiddenReason !== null) return proposal
  if (item.kind !== 'content' || parent === undefined) return proposal

  // The moment has to cover every day its content touches, including the days
  // the content stays in market.
  const reach =
    moved.inMarket &&
    !moved.inMarket.followsMoment &&
    moved.inMarket.end > moved.end
      ? moved.inMarket.end
      : moved.end
  if (reach <= parent.end) return proposal

  return {
    ...proposal,
    moved: moved.inMarket?.followsMoment
      ? { ...moved, inMarket: { ...moved.inMarket, end: reach } }
      : moved,
    parent: {
      next: { ...toChange(parent), end: reach },
      previous: toChange(parent),
    },
  }
}
